/**
 * 通知システムのZustandストア
 */

import { create } from 'zustand';
import { AppError, ErrorSeverity } from '../../types/error.types';

/**
 * 通知のタイプ
 */
export type NotificationType = 'info' | 'success' | 'warning' | 'error' | 'critical';

/**
 * 通知アクション
 */
export interface NotificationAction {
  /** アクションのラベル */
  label: string;
  /** アクションがクリックされた時の処理 */
  action: () => void;
  /** アクションのスタイル */
  style?: 'primary' | 'secondary' | 'danger';
}

/**
 * 通知データ
 */
export interface Notification {
  /** 通知の一意なID */
  id: string;
  /** 通知のタイプ */
  type: NotificationType;
  /** 通知のタイトル（オプション） */
  title?: string;
  /** 通知のメッセージ */
  message: string;
  /** 通知の詳細（オプション） */
  details?: string;
  /** 自動で消えるまでの時間（ミリ秒）。undefinedの場合は手動で閉じる必要あり */
  duration?: number;
  /** 通知に表示するアクション */
  actions?: NotificationAction[];
  /** 通知が作成された時刻 */
  timestamp: number;
  /** 通知を閉じることができるか */
  dismissible?: boolean;
  /** エラーオブジェクト（エラー通知の場合） */
  error?: AppError;
}

/**
 * 通知作成のオプション
 */
export type CreateNotificationOptions = Omit<Notification, 'id' | 'timestamp'>;

/**
 * 通知ストアの状態
 */
interface NotificationState {
  /** 現在表示中の通知リスト */
  notifications: Notification[];
  /** 通知の最大表示数 */
  maxNotifications: number;
  /** デフォルトの表示時間（ミリ秒） */
  defaultDuration: number;
}

/**
 * 通知ストアのアクション
 */
interface NotificationActions {
  /** 通知を表示 */
  show: (options: CreateNotificationOptions) => string;
  /** 情報通知を表示 */
  info: (message: string, options?: Partial<CreateNotificationOptions>) => string;
  /** 成功通知を表示 */
  success: (message: string, options?: Partial<CreateNotificationOptions>) => string;
  /** 警告通知を表示 */
  warning: (message: string, options?: Partial<CreateNotificationOptions>) => string;
  /** エラー通知を表示 */
  error: (message: string, options?: Partial<CreateNotificationOptions>) => string;
  /** AppErrorから通知を表示 */
  showError: (error: AppError) => string;
  /** 通知を削除 */
  dismiss: (id: string) => void;
  /** すべての通知を削除 */
  dismissAll: () => void;
  /** 特定タイプの通知をすべて削除 */
  dismissByType: (type: NotificationType) => void;
  /** 設定を更新 */
  updateSettings: (settings: Partial<Pick<NotificationState, 'maxNotifications' | 'defaultDuration'>>) => void;
}

/**
 * 通知ストア
 */
export const useNotificationStore = create<NotificationState & NotificationActions>((set, get) => ({
  // 状態
  notifications: [],
  maxNotifications: 5,
  defaultDuration: 5000,

  // アクション
  show: (options) => {
    const id = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const notification: Notification = {
      ...options,
      id,
      timestamp: Date.now(),
      dismissible: options.dismissible ?? true
    };

    set((state) => {
      const newNotifications = [...state.notifications, notification];
      // 最大数を超えた場合は古いものから削除
      if (newNotifications.length > state.maxNotifications) {
        newNotifications.splice(0, newNotifications.length - state.maxNotifications);
      }
      return { notifications: newNotifications };
    });

    // 自動削除の設定
    if (notification.duration) {
      setTimeout(() => {
        get().dismiss(id);
      }, notification.duration);
    }

    return id;
  },

  info: (message, options = {}) => {
    return get().show({
      type: 'info',
      message,
      duration: get().defaultDuration,
      ...options
    });
  },

  success: (message, options = {}) => {
    return get().show({
      type: 'success',
      message,
      duration: get().defaultDuration,
      ...options
    });
  },

  warning: (message, options = {}) => {
    return get().show({
      type: 'warning',
      message,
      duration: get().defaultDuration * 1.5, // 警告は少し長く表示
      ...options
    });
  },

  error: (message, options = {}) => {
    return get().show({
      type: 'error',
      message,
      duration: undefined, // エラーは手動で閉じる
      ...options
    });
  },

  showError: (error: AppError) => {
    // エラーの深刻度に応じた通知タイプを決定
    const typeMap: Record<ErrorSeverity, NotificationType> = {
      [ErrorSeverity.INFO]: 'info',
      [ErrorSeverity.WARNING]: 'warning',
      [ErrorSeverity.ERROR]: 'error',
      [ErrorSeverity.CRITICAL]: 'critical'
    };

    const type = typeMap[error.severity] || 'error';
    
    // 回復可能なエラーの場合はアクションを追加
    const actions: NotificationAction[] = [];
    if (error.recoverable) {
      actions.push({
        label: 'Dismiss',
        action: () => get().dismiss(error.id),
        style: 'secondary'
      });
    }

    return get().show({
      type,
      title: error.category.replace('_', ' ').toUpperCase(),
      message: error.getUserMessage(),
      details: error.message,
      duration: error.recoverable ? get().defaultDuration * 2 : undefined,
      actions,
      error,
      dismissible: error.recoverable
    });
  },

  dismiss: (id) => {
    set((state) => ({
      notifications: state.notifications.filter((n) => n.id !== id)
    }));
  },

  dismissAll: () => {
    set({ notifications: [] });
  },

  dismissByType: (type) => {
    set((state) => ({
      notifications: state.notifications.filter((n) => n.type !== type)
    }));
  },

  updateSettings: (settings) => {
    set((state) => ({
      ...state,
      ...settings
    }));
  }
}));

/**
 * 通知ストアのセレクター
 */
export const notificationSelectors = {
  /** エラー通知のみを取得 */
  getErrorNotifications: (state: NotificationState) =>
    state.notifications.filter((n) => n.type === 'error' || n.type === 'critical'),
  
  /** 警告通知のみを取得 */
  getWarningNotifications: (state: NotificationState) =>
    state.notifications.filter((n) => n.type === 'warning'),
  
  /** 成功通知のみを取得 */
  getSuccessNotifications: (state: NotificationState) =>
    state.notifications.filter((n) => n.type === 'success'),
  
  /** 情報通知のみを取得 */
  getInfoNotifications: (state: NotificationState) =>
    state.notifications.filter((n) => n.type === 'info'),
  
  /** 未読通知の数を取得 */
  getUnreadCount: (state: NotificationState) =>
    state.notifications.length,
  
  /** 最新の通知を取得 */
  getLatestNotification: (state: NotificationState) =>
    state.notifications[state.notifications.length - 1] || null
};

/**
 * エラーハンドラーとの連携設定
 */
export async function setupErrorHandlerIntegration(): Promise<void> {
  if (typeof window !== 'undefined') {
    const { rendererErrorHandler } = await import('../utils/error-handler');
    
    // エラーハンドラーに通知コールバックを設定
    rendererErrorHandler.setNotificationCallback((error: AppError) => {
      useNotificationStore.getState().showError(error);
    });

    // Web版ではIPCからのエラー通知は不要
  }
}